import { useState } from "react";
import { T, UI, FONT, BTN } from "../constants/tokens";
import { useCompanyProfileData } from "../utils/companyProfile";
import { useHoldingMarks, isAnswered, currentShare } from "../utils/holdingSegments";
import SegmentPicker from "./SegmentPicker";
import Icon from "./Icon";
import LTR from "./LTR";
import { regionLabelHe } from "../constants/regions";

// ─── What you marked, and how it moved ───────────────────────────────────────
// Sits on the holding card. Three states, all from stored rows:
//   unanswered → the question and one button
//   "not sure" → says so, offers the question again
//   marked     → the segment, its share when marked (fiscal year + filing),
//                its share in the latest filing, and the difference in points
//
// The "then" number is the snapshot the mark stored; the "now" number is
// computed from today's company_segments rows by the same rule the picker
// used. When the two come from the same fiscal year there is no movement to
// report and the card says exactly that instead of printing "0.0".
//
// No colour on the change: green and red are for the user's own gain and
// loss, and a company's segment mix moving is neither.
export default function HoldingMarkCard({ symbol }) {
  const { marks, loaded } = useHoldingMarks();
  const [picking, setPicking] = useState(false);
  const mark = marks[symbol] || null;
  const answered = isAnswered(marks, symbol);
  // Only a real mark needs today's rows; the hook is cached per symbol, so a
  // holding whose profile tab was opened costs nothing extra here.
  const data = useCompanyProfileData(mark && mark.segment_name ? symbol : null);
  const now = mark && mark.segment_name && data ? currentShare(mark, data.segments) : null;

  if (!loaded) return null;

  const frame = {
    background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`,
    borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: 16,
    display: "flex", flexDirection: "column", gap: 10,
  };
  const label = { fontSize: 12, fontWeight: 700, color: UI.text.muted };
  const btn = { ...BTN.secondary, padding: "10px 16px", minHeight: 44, fontSize: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 };

  let body;
  if (!answered) {
    body = (
      <>
        <div style={{ fontSize: 15, fontWeight: 700, color: UI.text.primary, fontFamily: FONT.heading }}>
          סמן תחום למעקב · <LTR style={{ fontFamily: FONT.latin }}>{symbol}</LTR>
        </div>
        <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.6 }}>
          בחירה אחת מתוך תחומי הפעילות שהחברה דיווחה · כשאירוע ייגע בתחום שסימנת, הכרטיס יגיד זאת
        </div>
        <button onClick={() => setPicking(true)} style={btn}>
          <Icon name="target" size={15}/> סמן
        </button>
      </>
    );
  } else if (!mark.segment_name) {
    body = (
      <>
        <div style={{ fontSize: 14, color: UI.text.secondary, lineHeight: 1.6 }}>
          סימנת: <span style={{ fontWeight: 700, color: UI.text.primary }}>עוד לא בטוח</span>
        </div>
        <button onClick={() => setPicking(true)} style={btn}>בחר עכשיו</button>
      </>
    );
  } else {
    const sameYear = now && now.fiscalYear === mark.marked_fiscal_year;
    const delta = now && !sameYear ? Math.round((now.pct - mark.marked_pct) * 10) / 10 : null;
    body = (
      <>
        <div style={{ fontSize: 15, fontWeight: 700, color: UI.text.primary, lineHeight: 1.4 }}>
          סימנת: {regionLabelHe(mark.segment_name)
            ? <span title={mark.segment_name}>{regionLabelHe(mark.segment_name)}</span>
            : <LTR style={{ unicodeBidi: "isolate" }}>{mark.segment_name}</LTR>}
        </div>
        {/* Two cells only when there is a newer report to set beside the mark.
            The same fiscal year in both is one number, not two equal ones side
            by side; no "now" yet is the mark alone. The line under it says why. */}
        {!(now && !sameYear) ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 12, color: UI.text.muted, marginBottom: 2 }}>{sameYear ? "" : "כשסימנת · "}שנת כספים <LTR>{mark.marked_fiscal_year}</LTR></div>
            <div className="si-num" style={{ fontSize: 18, fontWeight: 700, color: UI.text.primary }}><LTR>{mark.marked_pct}%</LTR></div>
          </div>
        ) : (
          /* Same two-cell, centred layout as "נתונים מלאים" below it. */
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 12, color: UI.text.muted, marginBottom: 2 }}>כשסימנת · שנת כספים <LTR>{mark.marked_fiscal_year}</LTR></div>
              <div className="si-num" style={{ fontSize: 18, fontWeight: 700, color: UI.text.primary }}><LTR>{mark.marked_pct}%</LTR></div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 12, color: UI.text.muted, marginBottom: 2 }}>בדוח האחרון · שנת כספים <LTR>{now.fiscalYear}</LTR></div>
              <div className="si-num" style={{ fontSize: 18, fontWeight: 700, color: UI.text.primary }}><LTR>{now.pct}%</LTR></div>
            </div>
          </div>
        )}
        <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.6 }}>
          {data === null ? null
            : !now ? "החברה לא דיווחה על תחום זה בפילוח האחרון · המספר שסימנת נשאר מהדוח שראית"
            : sameYear ? "אין עדיין דוח שנתי חדש להשוואה"
            : delta === 0 ? "ללא שינוי בין שני הדוחות"
            : <>שינוי של <LTR>{delta > 0 ? "+" : ""}{delta}</LTR> נקודות אחוז מאז שסימנת</>}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => setPicking(true)} style={btn}>שנה</button>
        </div>
        <div style={{ fontSize: 12, color: UI.text.muted, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <Icon name="clipboard" size={13} color={UI.text.muted}/>
          <span>מקור: <LTR style={{ fontWeight: 700, color: UI.text.secondary }}>SEC EDGAR</LTR> · האחוזים מתוך ההכנסות שהחברה פילחה</span>
        </div>
      </>
    );
  }

  return (
    <div style={frame}>
      {/* "התחום", not "האזור" (sprint 14, 4.1): the mark is one of the
          segments the company reports, geographic for some (Apple) and a line
          of business for others (Coca-Cola's Bottling Investments). */}
      {answered && <div style={label}>התחום שסימנת</div>}
      {body}
      {picking && (
        <SegmentPicker symbol={symbol} onDone={() => setPicking(false)} onClose={() => setPicking(false)}/>
      )}
    </div>
  );
}
